<template>
  <modal
    name="bulk-upload-modal"
    :scrollable="true"
    :max-width="700"
    width="50%"
    height="auto"
    @before-open="resetState"
  >
    <div class="bulk-upload-modal">
      <div class="bulk-upload-modal__header">
        <h2 class="bulk-upload-modal__title">Carga masiva de datos</h2>
        <i
          class="material-icons bulk-upload-modal__close-btn"
          @click="hideModal"
        >
          highlight_off
        </i>
      </div>

      <div class="bulk-upload-modal__body">
        <p class="bulk-upload-modal__description">
          Seleccione un archivo Excel (.xlsx) con sus pedidos. Puede descargar la plantilla
          para completar los datos correctamente.
        </p>

        <a
          class="bulk-upload-modal__template-link"
          :href="templateUrl"
          target="_blank"
          rel="noopener"
        >
          <i class="material-icons">file_download</i>
          Descargar plantilla
        </a>

        <label class="bulk-upload-modal__file-label">
          <input
            ref="fileInput"
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            class="bulk-upload-modal__file-input"
            :disabled="uploading"
            @change="onFileSelected"
          >
          <span class="bulk-upload-modal__file-button">Seleccionar archivo</span>
          <span
            v-if="selectedFile"
            class="bulk-upload-modal__file-name"
          >
            {{ selectedFile.name }}
          </span>
        </label>

        <div
          v-if="uploading || completed || failed"
          class="bulk-upload-modal__progress"
        >
          <div class="bulk-upload-modal__progress-bar">
            <div
              class="bulk-upload-modal__progress-fill"
              :style="{ width: `${progressPercent}%` }"
            />
          </div>
          <p class="bulk-upload-modal__progress-text">
            {{ progressText }}
          </p>
        </div>

        <p
          v-if="successMessage"
          class="bulk-upload-modal__success"
        >
          {{ successMessage }}
        </p>

        <p
          v-if="errorMessage"
          class="bulk-upload-modal__error"
        >
          {{ errorMessage }}
        </p>
      </div>

      <div class="bulk-upload-modal__actions">
        <button
          type="button"
          class="btn btn-outline"
          :disabled="uploading"
          @click="hideModal"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="btn"
          :disabled="!selectedFile || uploading || completed"
          @click="uploadFile"
        >
          Cargar
        </button>
      </div>
    </div>
  </modal>
</template>

<script>
import api from '../store/api';

export default {
  data() {
    return {
      selectedFile: null,
      uploading: false,
      completed: false,
      failed: false,
      progressPercent: 0,
      processedOrders: 0,
      totalOrders: 0,
      successMessage: '',
      errorMessage: '',
      pollTimer: null,
      templateUrl: '/profile/orders/bulk_upload_template',
    };
  },
  computed: {
    progressText() {
      if (this.failed) {
        return 'Proceso finalizado con errores';
      }
      if (this.completed) {
        return 'Proceso completado';
      }
      if (this.totalOrders > 0) {
        return `Procesando pedido ${this.processedOrders} de ${this.totalOrders} (${this.progressPercent}%)`;
      }
      return `Procesando... (${this.progressPercent}%)`;
    },
  },
  beforeDestroy() {
    this.clearPollTimer();
  },
  methods: {
    showModal() {
      this.$modal.show('bulk-upload-modal');
    },
    hideModal() {
      this.$modal.hide('bulk-upload-modal');
    },
    resetState() {
      this.clearPollTimer();
      this.selectedFile = null;
      this.uploading = false;
      this.completed = false;
      this.failed = false;
      this.progressPercent = 0;
      this.processedOrders = 0;
      this.totalOrders = 0;
      this.successMessage = '';
      this.errorMessage = '';
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
    onFileSelected(event) {
      const [file] = event.target.files;
      this.selectedFile = file || null;
      this.successMessage = '';
      this.errorMessage = '';
      this.completed = false;
      this.failed = false;
    },
    async uploadFile() {
      if (!this.selectedFile || this.uploading) return;

      this.uploading = true;
      this.errorMessage = '';
      this.successMessage = '';

      try {
        const formData = new FormData();
        formData.append('file', this.selectedFile);
        const response = await api.postBulkUpload(formData);
        this.startPolling(response.data.id);
      } catch (_error) {
        this.uploading = false;
        this.failed = true;
        this.errorMessage = 'No se pudo completar la carga. Revise el archivo e intente nuevamente.';
      }
    },
    startPolling(bulkUploadId) {
      this.clearPollTimer();
      this.pollTimer = setInterval(() => this.pollStatus(bulkUploadId), 2000);
      this.pollStatus(bulkUploadId);
    },
    async pollStatus(bulkUploadId) {
      try {
        const response = await api.getBulkUpload(bulkUploadId);
        const data = response.data;
        this.progressPercent = data.progress_percent || 0;
        this.processedOrders = data.processed_orders || 0;
        this.totalOrders = data.total_orders || 0;

        if (data.status === 'completed') {
          this.finishSuccess(data.created_count);
        } else if (data.status === 'failed') {
          this.finishError(data.error_message);
        }
      } catch (_error) {
        this.finishError();
      }
    },
    finishSuccess(createdCount) {
      this.clearPollTimer();
      this.uploading = false;
      this.completed = true;
      this.progressPercent = 100;
      this.successMessage = `Se crearon ${createdCount} órdenes. Actualice la página para verlas en su historial.`;
    },
    finishError(message) {
      this.clearPollTimer();
      this.uploading = false;
      this.failed = true;
      this.errorMessage = message || 'No se pudo completar la carga. Revise el archivo e intente nuevamente.';
    },
    clearPollTimer() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.bulk-upload-modal {
  padding: 20px;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
  }

  &__title {
    margin: 0;
    font-size: 1.25rem;
  }

  &__close-btn {
    cursor: pointer;
    color: #898989;
  }

  &__description {
    margin-bottom: 12px;
    color: #58585b;
  }

  &__template-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 16px;
    color: #50bfa3;
    text-decoration: underline;
    cursor: pointer;
  }

  &__file-label {
    display: block;
    margin-bottom: 16px;
  }

  &__file-input {
    display: none;
  }

  &__file-button {
    display: inline-block;
    padding: 8px 14px;
    border: 1px solid #d9d9d9;
    border-radius: 4px;
    cursor: pointer;
    background: #f8f8f8;
  }

  &__file-name {
    margin-left: 10px;
    color: #58585b;
  }

  &__progress {
    margin: 16px 0;
  }

  &__progress-bar {
    width: 100%;
    height: 10px;
    background: #ececec;
    border-radius: 6px;
    overflow: hidden;
  }

  &__progress-fill {
    height: 100%;
    background: #50bfa3;
    transition: width 0.3s ease;
  }

  &__progress-text {
    margin-top: 8px;
    font-size: 0.9rem;
    color: #58585b;
  }

  &__success {
    color: #2e7d32;
    margin-top: 12px;
  }

  &__error {
    color: #c62828;
    margin-top: 12px;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 20px;
  }
}
</style>
